import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Package, ChevronsUpDown, Check, Loader2, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useIntegrationService } from '@/hooks/use-integration-service';
import type { Asset } from '@/types/asset';
import type { Supplier, FinancialsSupplierAddress } from '@/types/financials';

interface AssetDetailSupplierProps {
  readonly asset: Asset;
  readonly onAssetUpdate: (asset: Asset) => void;
  readonly isEditMode?: boolean;
}

// Extra fields that are populated from supplier address but not persisted to Asset model
type ExtraSupplierFields = 'supplierContact' | 'supplierPhone' | 'supplierEmail' | 'hirePurchaseEndDate';
type AssetSupplierField = keyof Asset | ExtraSupplierFields;

export function AssetDetailSupplier({ asset, onAssetUpdate, isEditMode = false }: Readonly<AssetDetailSupplierProps>) {
  const { service } = useIntegrationService();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [suppliersError, setSuppliersError] = useState(false);
  const [, setSupplierAddresses] = useState<FinancialsSupplierAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [supplierPopoverOpen, setSupplierPopoverOpen] = useState(false);
  
  // Track the last supplierId we fetched addresses for to avoid double-fetching
  const lastFetchedSupplierIdRef = useRef<string | null>(null);
  const supplierDropdownRef = useRef<HTMLDivElement>(null);

  const handleFieldChange = (field: AssetSupplierField, value: unknown) => {
    onAssetUpdate({ ...asset, [field]: value } as Asset);
  };

  const handleDateChange = (field: AssetSupplierField, date: Date | undefined) => {
    handleFieldChange(field, date ? date.toISOString() : undefined);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
        setSupplierPopoverOpen(false);
      }
    };

    if (supplierPopoverOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [supplierPopoverOpen]);

  // Fetch suppliers on mount or when integration service changes
  useEffect(() => {
    const fetchSuppliers = async () => {
      setSuppliersLoading(true);
      setSuppliersError(false);
      try {
        const data = await service.getSuppliers();
        setSuppliers(data);
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
        setSuppliersError(true);
      } finally {
        setSuppliersLoading(false);
      }
    };

    fetchSuppliers();
  }, [service]);

  // Fetch supplier addresses on initial mount if asset already has a supplierId
  // This handles the case when editing an existing asset that already has a supplier
  // Note: handleSupplierSelect handles fetching when user selects a new supplier
  useEffect(() => {
    const fetchSupplierAddresses = async () => {
      if (!asset.supplierId) {
        setSupplierAddresses([]);
        lastFetchedSupplierIdRef.current = null;
        return;
      }

      // Skip if we already fetched addresses for this supplier (e.g., from handleSupplierSelect)
      if (lastFetchedSupplierIdRef.current === asset.supplierId) {
        return;
      }

      setAddressesLoading(true);
      try {
        const addresses = await service.getSupplierAddresses(asset.supplierId);
        setSupplierAddresses(addresses);
        lastFetchedSupplierIdRef.current = asset.supplierId;

        // Auto-populate contact fields from the primary address (order address preferred)
        // Only populate if the contact fields are empty (don't overwrite user edits)
        const assetRecord = asset as unknown as Record<string, unknown>;
        if (addresses.length > 0 && !assetRecord.supplierContact && !assetRecord.supplierPhone && !assetRecord.supplierEmail) {
          const primaryAddress = addresses.find(a => a.summarySection?.isOrder) || addresses[0];
          const updatedAsset = {
            ...asset,
            supplierContact: primaryAddress.summarySection?.completeContactName ?? '',
            supplierPhone: primaryAddress.details?.telephone ?? '',
            supplierEmail: primaryAddress.details?.email ?? '',
          };
          onAssetUpdate(updatedAsset as Asset);
        }
      } catch (error) {
        console.error('Failed to fetch supplier addresses:', error);
        setSupplierAddresses([]);
      } finally {
        setAddressesLoading(false);
      }
    };

    fetchSupplierAddresses();
  }, [asset.supplierId, service]);

  const handleSupplierSelect = async (supplier: Supplier) => {
    setSupplierPopoverOpen(false);
    setAddressesLoading(true);
    
    // Mark this supplier as fetched to prevent useEffect from double-fetching
    lastFetchedSupplierIdRef.current = supplier.code;
    
    try {
      const addresses = await service.getSupplierAddresses(supplier.code);
      setSupplierAddresses(addresses);
      
      // Build the updated asset with supplier info and auto-populated contact fields
      const updatedAsset: Record<string, unknown> = {
        ...asset,
        supplierId: supplier.code,
        supplierName: supplier.name ?? supplier.code,
      };
      
      // Auto-populate contact fields from the primary address (order address preferred)
      if (addresses.length > 0) {
        const primaryAddress = addresses.find(a => a.summarySection?.isOrder) || addresses[0];
        updatedAsset.supplierContact = primaryAddress.summarySection?.completeContactName ?? '';
        updatedAsset.supplierPhone = primaryAddress.details?.telephone ?? '';
        updatedAsset.supplierEmail = primaryAddress.details?.email ?? '';
      }
      
      onAssetUpdate(updatedAsset as unknown as Asset);
    } catch (error) {
      console.error('Failed to fetch supplier addresses:', error);
      // Still update the supplier even if addresses fail to load
      onAssetUpdate({
        ...asset,
        supplierId: supplier.code,
        supplierName: supplier.name ?? supplier.code,
      });
      setSupplierAddresses([]);
    } finally {
      setAddressesLoading(false);
    }
  };

  const getSelectedSupplierDisplay = () => {
    if (suppliersLoading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (asset.supplierId) {
      const selectedSupplier = suppliers.find(s => s.code === asset.supplierId);
      return selectedSupplier
        ? `${selectedSupplier.code} - ${selectedSupplier.name}`
        : asset.supplierName ?? asset.supplierId;
    }
    return "Select supplier...";
  };

  const hirePurchaseEndDate = (asset as unknown as Record<string, unknown>).hirePurchaseEndDate as string | undefined;
  const hirePurchaseEndDateValue = hirePurchaseEndDate ? new Date(hirePurchaseEndDate) : undefined;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Supplier Information
          </CardTitle>
          <CardDescription className="text-xs">
            Supplier details and purchase order references
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplierName">Supplier</Label>
            {isEditMode ? (
              <div className="relative" ref={supplierDropdownRef}>
                <Input
                  id="supplierName"
                  value={asset.supplierName ?? ''}
                  onChange={(e) => handleFieldChange('supplierName', e.target.value)}
                  placeholder="Enter supplier name or select from list"
                  disabled={suppliersLoading}
                  onFocus={() => setSupplierPopoverOpen(true)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setSupplierPopoverOpen(!supplierPopoverOpen)}
                  disabled={suppliersLoading}
                >
                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </Button>
                {supplierPopoverOpen && suppliers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-64 overflow-y-auto">
                    <Command>
                      <CommandInput placeholder="Search suppliers..." />
                      <CommandEmpty>No supplier found.</CommandEmpty>
                      <CommandGroup>
                        {suppliers.map((supplier) => (
                          <CommandItem
                            key={supplier.primaryKey}
                            value={`${supplier.code} ${supplier.name ?? ""}`}
                            onSelect={() => handleSupplierSelect(supplier)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                asset.supplierId === supplier.code ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <span className="font-medium">{supplier.code}</span>
                            <span className="ml-2 text-muted-foreground">{supplier.name}</span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                  </div>
                )}
              </div>
            ) : (
              <Input
                id="supplierName"
                value={asset.supplierName ?? ''}
                readOnly
              />
            )}
            {suppliersError && (
              <p className="text-sm text-destructive">Failed to load suppliers</p>
            )}
            {addressesLoading && (
              <p className="text-sm text-muted-foreground flex items-center">
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Loading supplier addresses...
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="purchaseOrderNumber">Purchase Order Number</Label>
            {isEditMode ? (
              <Input
                id="purchaseOrderNumber"
                value={asset.purchaseOrderNumber ?? ''}
                onChange={(e) => handleFieldChange('purchaseOrderNumber', e.target.value)}
                placeholder="Enter PO number"
              />
            ) : (
              <Input
                id="purchaseOrderNumber"
                value={asset.purchaseOrderNumber ?? ''}
                readOnly
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="invoiceReference">Invoice Reference</Label>
            {isEditMode ? (
              <Input
                id="invoiceReference"
                value={asset.invoiceReference ?? ''}
                onChange={(e) => handleFieldChange('invoiceReference', e.target.value)}
                placeholder="Enter invoice reference"
              />
            ) : (
              <Input
                id="invoiceReference"
                value={asset.invoiceReference ?? ''}
                readOnly
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription className="text-xs">
            Supplier contact details (auto-populated from supplier address)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supplierContact">Contact Name</Label>
            {isEditMode ? (
              <Input
                id="supplierContact"
                value={((asset as unknown as Record<string, unknown>).supplierContact as string) ?? ''}
                onChange={(e) => handleFieldChange('supplierContact', e.target.value)}
                placeholder="Enter contact name"
              />
            ) : (
              <Input
                id="supplierContact"
                value={((asset as unknown as Record<string, unknown>).supplierContact as string) ?? ''}
                readOnly
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplierPhone">Phone</Label>
            {isEditMode ? (
              <Input
                id="supplierPhone"
                value={((asset as unknown as Record<string, unknown>).supplierPhone as string) ?? ''}
                onChange={(e) => handleFieldChange('supplierPhone', e.target.value)}
                placeholder="Enter phone number"
              />
            ) : (
              <Input
                id="supplierPhone"
                value={((asset as unknown as Record<string, unknown>).supplierPhone as string) ?? ''}
                readOnly
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="supplierEmail">Email</Label>
            {isEditMode ? (
              <Input
                id="supplierEmail"
                type="email"
                value={((asset as unknown as Record<string, unknown>).supplierEmail as string) ?? ''}
                onChange={(e) => handleFieldChange('supplierEmail', e.target.value)}
                placeholder="Enter email address"
              />
            ) : (
              <Input
                id="supplierEmail"
                value={((asset as unknown as Record<string, unknown>).supplierEmail as string) ?? ''}
                readOnly
              />
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Hire Purchase
          </CardTitle>
          <CardDescription className="text-xs">
            Hire purchase agreement status and end date
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="hirePurchase">Hire Purchase</Label>
              <div className="text-sm text-muted-foreground">
                Is this asset on hire purchase?
              </div>
            </div>
            <Switch
              id="hirePurchase"
              checked={asset.hirePurchase ?? false}
              onCheckedChange={(checked) => handleFieldChange('hirePurchase', checked)}
              disabled={!isEditMode}
            />
          </div>

          {asset.hirePurchase && (
            <div className="space-y-2">
              <Label htmlFor="hirePurchaseEndDate">Hire Purchase End Date</Label>
              {isEditMode ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="hirePurchaseEndDate"
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !hirePurchaseEndDateValue && 'text-muted-foreground'
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {hirePurchaseEndDateValue ? format(hirePurchaseEndDateValue, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={hirePurchaseEndDateValue}
                      onSelect={(date) => handleDateChange('hirePurchaseEndDate', date)}
                      autoFocus
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <Input
                  id="hirePurchaseEndDate"
                  value={hirePurchaseEndDateValue ? format(hirePurchaseEndDateValue, 'PPP') : ''}
                  readOnly
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
